import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { ServerErrorPage } from '../pages/public/ErrorPages';

/**
 * La red que evita la pantalla en blanco.
 *
 * Sin un límite de error, **cualquier** excepción lanzada al renderizar
 * desmonta la aplicación entera y deja un `<div id="root">` vacío: ni mensaje,
 * ni marca, ni forma de salir salvo escribir otra URL a mano. Este proyecto ya
 * se lo comió una vez —el orden de los proveedores dejaba `ToastProvider`
 * fuera del idioma y el primer aviso tumbaba la pantalla; está contado en
 * `providers.tsx`— y entonces no había nada que lo amortiguara.
 *
 * Lo que se pinta cuando pasa es `ServerErrorPage`, que llevaba escrita y
 * enrutada en `/500` desde el principio **sin que nada pudiera mostrarla**: no
 * había ni un `Navigate` ni un límite que la usara, así que sólo se veía
 * escribiendo la dirección. Aquí encuentra su trabajo.
 *
 * Dos decisiones que no son obvias:
 *
 *  - **Va dentro del proveedor de idioma.** `ServerErrorPage` traduce con
 *    `useT()`, así que por fuera de `LocaleBridge` el propio respaldo lanzaría
 *    y volveríamos al blanco. Eso deja los proveedores sin cubrir, y es
 *    aceptable: son cuatro líneas con su propio test en `providers.test.tsx`.
 *  - **Se reinicia al cambiar de ruta.** Es la trampa clásica: el respaldo
 *    ofrece «Ir al inicio», la navegación ocurre, pero el estado del límite
 *    sigue en error y se queda enseñando la misma pantalla. Con el botón
 *    muerto, el error deja de ser recuperable y vuelve a ser un callejón.
 */

interface Props {
  children: ReactNode;
  /** Al cambiar, se limpia el error. Aquí es la ruta. */
  resetKey: string;
  /** Sólo para los tests: dónde se reporta el fallo. */
  onError?: (error: Error, info: ErrorInfo) => void;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    /*
     * Se registra en consola porque hoy no hay ningún destino de errores
     * —no hay Sentry ni equivalente— y la consola del navegador es el único
     * sitio donde esto queda. El `componentStack` es la mitad útil: dice qué
     * componente lanzó, que es lo que el mensaje de error solo no dice.
     */
    console.error('[pantalla] error al renderizar:', error, info.componentStack);
    this.props.onError?.(error, info);
  }

  override componentDidUpdate(anterior: Props): void {
    // Sólo al navegar, y sólo si había error: así no se remonta el árbol
    // entero en cada cambio de ruta, que es lo que haría un `key`.
    if (this.state.error && anterior.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  override render(): ReactNode {
    return this.state.error ? <ServerErrorPage /> : this.props.children;
  }
}

/**
 * El límite, atado a la ruta actual.
 *
 * Se separa del componente de clase para que la clase siga siendo probable sin
 * router: un test puede pasarle `resetKey` a mano y comprobar el reinicio sin
 * montar navegación de verdad.
 */
export function RouteErrorBoundary({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();

  return <ErrorBoundary resetKey={pathname}>{children}</ErrorBoundary>;
}
